<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Animais</h5>
      <b-button
        v-if="hasPermission('adicionar_animais')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Novo Cadastro"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por nome"
        class="form-control"
        @input="filtrarAnimais"
      />
      <select
        v-model="filters.ativo"
        class="form-select"
        style="max-width: 200px;"
        @change="filtrarAnimais"
      >
        <option :value="undefined">Filtrar por ativo</option>
        <option :value="true">Ativo</option>
        <option :value="false">Inativo</option>
      </select>
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('id')">
              Id {{ arrow('id') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('nome')">
              Nome {{ arrow('nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('sexo')">
              Sexo {{ arrow('sexo') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('raca')">
              Raça {{ arrow('raca') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('nascimento')">
              Nascimento {{ arrow('nascimento') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('ativo')">
              Ativo {{ arrow('ativo') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in animais" :key="row.id">
          <td>{{ row.id }}</td>
          <td>{{ row.nome }}</td>
          <td>{{ row.sexo }}</td>
          <td>{{ row.raca }}</td>
          <td>{{ globalUtils.formatarData(row.nascimento) }}</td>
          <td>{{ row.ativo ? 'Ativo' : 'Inativo' }}</td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_animais')"
              class="bi bi-eye-fill text-primary mx-2"
              title="Detalhar"
              @click="detalharAnimal(row.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_animais')"
              class="bi bi-pencil-fill text-warning mx-2"
              title="Editar"
              @click="editarAnimal(row.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_animais')"
              class="bi bi-trash-fill text-danger mx-2"
              title="Excluir"
              @click="excluirAnimal(row.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="[10,15,20,25,50].map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="onLimitChange"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'
import globalUtils from '@/utils/globalUtils.js'

const router = useRouter()
const { proxy } = getCurrentInstance()

const {
  items: animais,
  totalItems,
  page: paginaAtual,
  limit: limitePorPagina,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/animais`,
  { key: 'nome', order: 'asc' }
)

function onLimitChange() {
  paginaAtual.value = 1
  load()
}

// Watch para disparar load() em qualquer mudança de página
watch(paginaAtual, () => {
  load()
})

const startRow = computed(() =>
  (paginaAtual.value - 1) * limitePorPagina.value + 1
)
const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalItems.value)
)

function filtrarAnimais() {
  paginaAtual.value = 1
  load()
}

function limparFiltros() {
  filters.value = { nome: '', ativo: undefined }
  paginaAtual.value = 1
  load()
}

function irParaCadastro() {
  router.push('/cadastros/animais/novo')
}

function detalharAnimal(id) {
  router.push(`/cadastros/animais/${id}/detalhar`)
}

function editarAnimal(id) {
  router.push(`/cadastros/animais/${id}`)
}

async function excluirAnimal(id) {
  const confirmou = await proxy.$confirm(
    'Tem certeza que deseja excluir este animal?',
    'Confirmação'
  )
  if (!confirmou) return
  try {
    await axios.delete(`/cadastros/animais/${id}`)
    await load()
    proxy.$toast.success('Animal excluído com sucesso!')
  } catch (e) {
    const msg = e.response?.data?.error || 'Erro ao excluir animal.'
    await proxy.$alert(msg, 'Erro')
  }
}

onMounted(() => {
  filters.value = { nome: '', ativo: undefined }
  load()
})
</script>

<style scoped>

:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

</style>
